// biome-ignore lint/performance/noNamespaceImport: Required for library
import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import type { CTWRequestContext } from '@ctw/shared/context/ctw-context';
import type { TelemetryContext } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { mapTelemetryContext } from '@ctw/shared/context/telemetry/telemetry-context-mapper';
import type { TimerMetricIdentifier } from '@ctw/shared/context/telemetry/timer-metrics';
import type { TelemetryUserInteraction } from '@ctw/shared/context/telemetry/user-interactions';
import type { ZusService } from '@ctw/shared/context/zui-provider';
import { DelegatedLogger } from '@ctw/shared/utils/delegated-logger';
import {
  currentHostnameIsLocalDevEnvironment,
  environmentNames,
  getApiServiceBaseUrl,
} from '@ctw/shared/utils/environments';
import type { WithLimitedKeys } from '@ctw/shared/utils/types';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { cloneDeep, entries, isError } from 'lodash-es';
import type { Diff, Intersection } from 'utility-types';
import { RequestError } from '../../utils/error';
import { ReactQueryError } from '../../utils/react-query-error';
import { Session } from '../../utils/session';

amplitude.add(engagementPlugin());

declare global {
  interface Window {
    __zus_telemetry: Telemetry;
    _zus_dev_telemetry_enabled: boolean | undefined;
  }
}

export interface WithTimerMetricOperationFnContext {
  setScopedContextProperties: (properties: TelemetryContext) => void;
  setScopedContextProperty: (name: string, value: unknown) => void;
  getScopedContextProperties: () => TelemetryContext;
  getScopedContextProperty: (name: string) => unknown;
}

export interface StartTimerMetricContext {
  setScopedContextProperties: (properties: TelemetryContext) => void;
  setScopedContextProperty: (name: string, value: unknown) => void;
  getScopedContextProperties: () => TelemetryContext;
  getScopedContextProperty: (name: string) => unknown;
}

export type TelemetryService = 'datadogLogs' | 'datadogRum' | 'amplitude';
type TelemetryMetricType = 'loading' | 'query' | 'render_component' | 'render_page';
type TelemetryEnv = 'dev' | 'prod';

type TelemetryInteractionCondition = 'every_occurence' | 'once_per_session' | 'once_per_page_view';

interface TelemetryInitArgs {
  requestContext: CTWRequestContext;
}

type TelemetryInterface = typeof Telemetry;
interface DeferredTelemetryEvents {
  setGlobalContextProperty: Array<Parameters<TelemetryInterface['setGlobalContextProperty']>>;
  trackError: Array<Parameters<TelemetryInterface['trackError']>[0]>;
  trackInteractionWithCondition: Array<
    Parameters<TelemetryInterface['trackInteractionWithCondition']>
  >;
  trackMetric: Array<Parameters<TelemetryInterface['trackMetric']>>;
  logger: Array<
    | ['debug', ...Parameters<DelegatedLogger['debug']>]
    | ['info', ...Parameters<DelegatedLogger['info']>]
    | ['warn', ...Parameters<DelegatedLogger['warn']>]
    | ['error', ...Parameters<DelegatedLogger['error']>]
  >;
}

type AmplitudeInitConfiguration = WithLimitedKeys<{
  apiKey: string;
  options: WithLimitedKeys<Exclude<Parameters<typeof amplitude.init>[2], undefined>>;
}>;

type AmplitudeCommonInitConfiguration = Omit<WithLimitedKeys<AmplitudeInitConfiguration>, 'apiKey'>;

type InternalDatadogLogsInitConfiguration = Parameters<typeof datadogLogs.init>[0];
type InternalDatadogRumInitConfiguration = Parameters<typeof datadogRum.init>[0];
type DatadogCommonInitConfiguration = WithLimitedKeys<
  Intersection<InternalDatadogLogsInitConfiguration, InternalDatadogRumInitConfiguration>
>;
type DatadogLogsInitConfiguration = WithLimitedKeys<
  Diff<InternalDatadogLogsInitConfiguration, DatadogCommonInitConfiguration>
>;
type DatadogRumInitConfiguration = WithLimitedKeys<
  Diff<InternalDatadogRumInitConfiguration, DatadogCommonInitConfiguration>
>;

type DatadogTelemetryServiceConfig = {
  global: WithLimitedKeys<DatadogCommonInitConfiguration>;
  logs: WithLimitedKeys<DatadogLogsInitConfiguration>;
  rum: WithLimitedKeys<DatadogRumInitConfiguration>;
};

type CommonDatadogTelemetryServiceConfig = {
  global: Partial<DatadogTelemetryServiceConfig['global']>;
  logs: Partial<DatadogTelemetryServiceConfig['logs']>;
  rum: Partial<DatadogTelemetryServiceConfig['rum']>;
};

type TelemetryConfigs = {
  [env in TelemetryEnv]: {
    [service in ZusService]: {
      amplitude: AmplitudeInitConfiguration;
      datadog: WithLimitedKeys<DatadogTelemetryServiceConfig>;
    };
  };
};

const telemetryEnvPatternMappings = {
  dev: [/^([a-z]+)\.(dev|phitest)\.(zushealth|zusapi)\.com$/i, /localhost/i, /127\.0\.0\.1/i],
  prod: [/^([a-z]+)\.(sandbox\.)?(zushealth|zusapi)\.com$/i],
} as const;

// Enables Datadog RUM to inject trace-correlation headers into FQS GraphQL requests, so a
// browser session can be followed into its FQS APM trace. Scoped to `/query`.
//
// Known limitation: this list matches the patients list page's `usePatientsList`, but that
// query can initiate before `TelemetryInitializer` initializes RUM and so won't carry these
// headers. Every other `/query` call is gated behind `PatientProvider` resolving the patient
// first, well after RUM is instrumented.
const fqsGraphQlAllowedTracingUrls = environmentNames.map(
  (env) => `${getApiServiceBaseUrl({ service: 'fqs', env })}/query`,
);

const datadogCommonEnvironmentConfig: CommonDatadogTelemetryServiceConfig = {
  global: {
    site: 'datadoghq.com',
    useSecureSessionCookie: true,
    usePartitionedCrossSiteSessionCookie: true,
    telemetrySampleRate: 100,
    telemetryConfigurationSampleRate: 100,
    telemetryUsageSampleRate: 100,
    sessionSampleRate: 100,
  },
  logs: {
    forwardConsoleLogs: ['error'],
    forwardErrorsToLogs: true,
  },
  rum: {
    allowedTracingUrls: fqsGraphQlAllowedTracingUrls,
    defaultPrivacyLevel: 'mask',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    sessionReplaySampleRate: 100,
  },
} as const;

const datadogDevEnvironmentConfig: DatadogTelemetryServiceConfig = {
  global: {
    clientToken: 'pub29b659b1cd402a88d57c4f8c923c1eea', // gitleaks:allow
    ...datadogCommonEnvironmentConfig.global,
  },
  logs: {
    ...datadogCommonEnvironmentConfig.logs,
  },
  rum: {
    applicationId: '48fec1f8-b187-492a-afdd-e809cc6b3b82', // gitleaks:allow
    ...datadogCommonEnvironmentConfig.rum,
  },
} as const;

const datadogProdEnvironmentConfig: DatadogTelemetryServiceConfig = {
  global: {
    clientToken: 'pub6c5b408646325f595789b3e8127211fd', // gitleaks:allow
    ...datadogCommonEnvironmentConfig.global,
  },
  logs: {
    ...datadogCommonEnvironmentConfig.logs,
  },
  rum: {
    applicationId: '3dfeeabc-d289-4fe4-8118-06c9817f3c27', // gitleaks:allow
    ...datadogCommonEnvironmentConfig.rum,
  },
} as const;

const amplitudeCommonEnvironmentConfig: AmplitudeCommonInitConfiguration = {
  options: {
    logLevel: ['localhost', '127.0.0.1'].some((host) =>
      typeof window === 'undefined' ? false : window.location.host.includes(host),
    )
      ? amplitude.Types.LogLevel.Verbose
      : amplitude.Types.LogLevel.Warn,
    autocapture: {
      attribution: false,
      pageViews: false,
      sessions: true,
      formInteractions: true,
      fileDownloads: true,
      elementInteractions: false,
    },
  },
};

const amplitudeDevEnvironmentConfig: AmplitudeInitConfiguration = {
  // Amplitude project id: 663569
  apiKey: '4aa3735b24424ff8f01536ee58883f90', // gitleaks:allow
  options: {
    ...amplitudeCommonEnvironmentConfig.options,
  },
} as const;

const amplitudeProdEnvironmentConfig: AmplitudeInitConfiguration = {
  // Amplitude project id: 491330
  apiKey: 'a02c1e71c9a5a05a85d07589468ec2b1', // gitleaks:allow
  options: {
    ...amplitudeCommonEnvironmentConfig.options,
  },
} as const;

const telemetryConfigs: TelemetryConfigs = {
  dev: {
    standalone: {
      amplitude: amplitudeDevEnvironmentConfig,
      datadog: datadogDevEnvironmentConfig,
    },
    zap: {
      amplitude: amplitudeDevEnvironmentConfig,
      datadog: datadogDevEnvironmentConfig,
    },
    'smart-on-fhir': {
      amplitude: amplitudeDevEnvironmentConfig,
      datadog: datadogDevEnvironmentConfig,
    },
  },
  prod: {
    standalone: {
      amplitude: amplitudeProdEnvironmentConfig,
      datadog: datadogProdEnvironmentConfig,
    },
    zap: {
      amplitude: amplitudeProdEnvironmentConfig,
      datadog: datadogProdEnvironmentConfig,
    },
    'smart-on-fhir': {
      amplitude: amplitudeProdEnvironmentConfig,
      datadog: datadogProdEnvironmentConfig,
    },
  },
} as const;

const REDACTED_HEADERS = [
  'cookie',
  'set-cookie',
  'x-middleware-set-cookie',
  'authorization',
] as const;

// Response header set by the API to indicate whether the response was served
// from hot storage. Surfaced onto automatic RUM resource telemetry below.
const HOT_STORAGE_USED_HEADER = 'x-hotstorage-used';

/**
 * Telemetry via DataDog Logs, DataDog RUM, and Amplitude.
 */
export class Telemetry {
  public static logger = new DelegatedLogger({
    beforeDebug: (tag, message, context) => {
      if (this.isInitialized) {
        datadogLogs.logger.debug(`[${tag}] ${message}`, context);
      } else {
        this.deferredEvents.logger.push(['debug', tag, message, context]);
      }

      return { logToConsole: this.telemetryEnv === 'dev' };
    },
    beforeInfo: (tag, message, context) => {
      if (this.isInitialized) {
        datadogLogs.logger.info(`[${tag}] ${message}`, context);
      } else {
        this.deferredEvents.logger.push(['info', tag, message, context]);
      }

      return { logToConsole: this.telemetryEnv === 'dev' };
    },
    beforeWarn: (tag, message, context) => {
      if (this.isInitialized) {
        datadogLogs.logger.warn(`[${tag}] ${message}`, context);
      } else {
        this.deferredEvents.logger.push(['warn', tag, message, context]);
      }

      return { logToConsole: this.telemetryEnv === 'dev' };
    },
    beforeError: (tag, message, context) => {
      if (this.isInitialized) {
        datadogLogs.logger.error(`[${tag}] ${isError(message) ? message.message : message}`, {
          ...context,
          ...(typeof message === 'string' ? {} : { error: message }),
        });
      } else {
        this.deferredEvents.logger.push(['error', tag, message, context]);
      }

      return { logToConsole: this.telemetryEnv === 'dev' };
    },
  });

  private static configHash?: number;

  private static requestContext: CTWRequestContext;

  private static telemetryEnv: TelemetryEnv;

  private static interactionLoggingEnabled = false;

  private static deferredEvents: DeferredTelemetryEvents = {
    setGlobalContextProperty: [],
    trackError: [],
    trackInteractionWithCondition: [],
    trackMetric: [],
    logger: [],
  };

  private static previouslyTrackedPageInteractions: Array<string> = [];

  private static previouslyTrackedSessionInteractions: Array<string> = [];

  private static timerMetrics: Record<
    string,
    {
      type: TelemetryMetricType;
      id: string;
      vitalName: string;
      context: TelemetryContext;
      startTime: number;
      endTime?: number;
    }
  > = {};

  private static globalContextProperties: TelemetryContext = {};

  // The most recent view name requested via setViewName(), retained so it can be
  // (re)applied to DataDog RUM once init completes. Only the latest value matters,
  // so this is a single slot rather than a deferred-event queue.
  private static pendingViewName?: string;

  public static isInitialized = false;

  private static reinitializationCount = 0;
  private static isDatadogRumInitialized = false;
  private static isDatadogLoggingInitialized = false;
  private static isAmplitudeInitialized = false;

  // Serializes concurrent init() calls (e.g., React StrictMode double-invocation
  // or rapid requestContext changes) so SDK init blocks don't race past their
  // "already initialized?" guards and double-initialize.
  private static initPromise?: Promise<void>;

  /**
   * Initializes the Telemetry module. This method sets up the necessary configurations
   * for telemetry tracking, including initializing logging and analytics services
   * (e.g., DataDog, Amplitude) and restoring any previously tracked session interactions.
   * It ensures telemetry is correctly configured based on the current environment
   * and service context.
   *
   * Concurrent calls share a single in-flight promise; once the promise settles,
   * `initPromise` is cleared so a later call (e.g., after `requestContext` changes)
   * can run again. Failures in individual SDK inits are caught so the remaining
   * SDKs still initialize, `isInitialized` still flips to `true`, and buffered
   * events are still flushed.
   *
   * @param {TelemetryInitArgs} args - The initialization arguments for Telemetry.
   * @param {Object} args.requestContext - The request context containing service, environment,
   *     authentication, and other related parameters needed for initializing telemetry.
   *
   * @return {Promise<void>} A promise that resolves once all asynchronous initialization
   *     tasks are complete.
   */
  public static init(args: TelemetryInitArgs): Promise<void> {
    if (Telemetry.initPromise) {
      return Telemetry.initPromise;
    }

    Telemetry.initPromise = Telemetry.performInit(args).finally(() => {
      Telemetry.initPromise = undefined;
    });

    return Telemetry.initPromise;
  }

  private static async performInit({ requestContext }: TelemetryInitArgs): Promise<void> {
    try {
      Telemetry.requestContext = cloneDeep(requestContext);
      Telemetry.telemetryEnv = Telemetry.determineTelemetryEnv();
      Telemetry.interactionLoggingEnabled = requestContext.serviceEnv !== 'prod';

      if (Telemetry.interactionLoggingEnabled) {
        Telemetry.logger.debug('Telemetry', 'init', {
          requestContext,
          telemetryEnv: Telemetry.telemetryEnv,
        });
      }

      // Restore previously tracked session interactions from local storage if possible
      try {
        const previouslyTrackedSessionInteractionsJson =
          window.sessionStorage.getItem('previouslyTrackedSessionInteractions') ?? '[]';
        const previouslyTrackedSessionInteractionsJsonArray = JSON.parse(
          previouslyTrackedSessionInteractionsJson,
        );

        if (Array.isArray(previouslyTrackedSessionInteractionsJsonArray)) {
          Telemetry.previouslyTrackedSessionInteractions =
            previouslyTrackedSessionInteractionsJsonArray;
        }
      } catch {
        // On failure, try to delete the key in case it is corrupted
        try {
          window.sessionStorage.removeItem('previouslyTrackedSessionInteractions');
        } catch {
          /* pass */
        }
      }

      // Initialize DataDog logging (if it is not already initialized). Isolated so
      // a failure here doesn't prevent Amplitude/RUM from initializing.
      try {
        if (datadogLogs.getInternalContext() === undefined) {
          // https://docs.datadoghq.com/logs/log_collection/javascript/#configuration
          const { logs: logsConf, global: globalConf } =
            telemetryConfigs[Telemetry.telemetryEnv][Telemetry.requestContext.service].datadog;
          datadogLogs.init({
            ...globalConf,
            ...logsConf,
            env: Telemetry.requestContext.serviceEnv,
            service: Telemetry.requestContext.service,
            version: Telemetry.requestContext.serviceVersion,
          });
          Telemetry.isDatadogLoggingInitialized = true;
        }

        if (Telemetry.requestContext.serviceEnv === 'prod') {
          datadogLogs.logger.setLevel('info');
        }
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'datadogLogs init failed', { error });
      }

      // Initialize DataDog RUM if telemetry is enabled (if it is not already initialized).
      // Done before Amplitude so a rejected Amplitude init can't block RUM setup.
      try {
        if (Telemetry.telemetryEnabled() && datadogRum.getInternalContext() === undefined) {
          // https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/
          datadogRum.init({
            ...telemetryConfigs[Telemetry.telemetryEnv][requestContext.service].datadog.global,
            ...telemetryConfigs[Telemetry.telemetryEnv][requestContext.service].datadog.rum,
            env: Telemetry.requestContext.serviceEnv,
            service: Telemetry.requestContext.service,
            version: Telemetry.requestContext.serviceVersion,
            beforeSend: (event, context) => {
              if (
                // Don't send errors for aborted requests
                context &&
                'isAborted' in context &&
                context.isAborted &&
                event.type === 'resource' &&
                event.resource.status_code === 0
              ) {
                return false;
              }

              // Enrich automatic resource telemetry for API calls (ctwFetch and
              // graphqlClient are both fetch-based, so both flow through here) with
              // whether the response was served from hot storage. Defaults to
              // `false` when the `x-hotstorage-used` header is absent so the
              // attribute is always present and queryable in DataDog.
              if (event.type === 'resource') {
                let hotStorageHeader: string | null | undefined;
                if ('response' in context) {
                  hotStorageHeader = context.response?.headers.get(HOT_STORAGE_USED_HEADER) ?? null;
                } else if ('xhr' in context) {
                  hotStorageHeader = context.xhr.getResponseHeader(HOT_STORAGE_USED_HEADER);
                }

                // Only tag fetch/XHR resources (network requests); leaves other
                // resource kinds like images, CSS, and scripts untouched.
                if (hotStorageHeader !== undefined) {
                  event.context = {
                    ...event.context,
                    hotStorageUsed: hotStorageHeader === 'true',
                  };
                }
              }

              return true;
            },
          });
          Telemetry.isDatadogRumInitialized = true;
        }
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'datadogRum init failed', { error });
      }

      // Initialize Amplitude if telemetry is enabled (if it is not already initialized).
      // Isolated so a rejected Amplitude init can't prevent DataDog events from being sent.
      try {
        if (Telemetry.telemetryEnabled() && amplitude.getSessionId() === undefined) {
          // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2
          await amplitude.init(
            telemetryConfigs[Telemetry.telemetryEnv][Telemetry.requestContext.service].amplitude
              .apiKey,
            {
              ...telemetryConfigs[Telemetry.telemetryEnv][Telemetry.requestContext.service]
                .amplitude.options,
              instanceName: requestContext.env,
              appVersion: requestContext.serviceVersion,
              userId: requestContext.authTokenState.zusUserId,
              logLevel: amplitude.Types.LogLevel.Warn,
            },
          ).promise;

          Telemetry.isAmplitudeInitialized = true;
        }
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'amplitude init failed', { error });
      }

      // After DataDog and Amplitude are initialized, set initial global context properties
      Telemetry.initGlobalContextProperties();

      // Warn when Telemetry is disabled for the current environment
      if (!Telemetry.telemetryEnabled() && Telemetry.configHash === undefined) {
        Telemetry.logger.info(
          'Telemetry',
          'Telemetry data sending is disabled in this environment',
          {
            service: Telemetry.requestContext.service,
            serviceEnv: Telemetry.requestContext.serviceEnv,
            telemetryEnv: Telemetry.telemetryEnv,
          },
        );
      }

      // Warn when Telemetry is reinitialized with a different configuration - this is not supported
      // and may cause unexpected behavior.
      const newConfigHash = Telemetry.getConfigHash(requestContext);
      if (Telemetry.isInitialized && Telemetry.configHash !== newConfigHash) {
        Telemetry.reinitializationCount += 1;
        Telemetry.configHash = newConfigHash;

        Telemetry.logger.warn('Telemetry', 'Reinitializing telemetry with new configuration', {
          service: Telemetry.requestContext.service,
          serviceEnv: Telemetry.requestContext.serviceEnv,
          telemetryEnv: Telemetry.telemetryEnv,
        });
      }

      // Assign Telemetry to a global variable so that it can be accessed in deployed environments for
      // debugging purposes - most commonly `window.__zus_telemetry.debugState()`
      if (typeof window !== 'undefined') {
        window.__zus_telemetry = Telemetry;
      }
    } catch (error) {
      // Catch any unexpected error from non-SDK init steps so we still flip
      // `isInitialized` and drain buffered events below.
      Telemetry.logger.error('Telemetry', 'init failed', { error });
    } finally {
      Telemetry.isInitialized = true;
      Telemetry.flushDeferredEvents();

      // Apply the latest requested view name now that RUM is initialized. The
      // route effect that requests it usually fires before this async init
      // resolves, so this is what actually names the first view.
      if (Telemetry.pendingViewName !== undefined && Telemetry.telemetryEnabled()) {
        datadogRum.setViewName(Telemetry.pendingViewName);
      }
    }
  }

  public static setPatient({
    patientID,
    patientResourceID,
    systemURL,
    patientUPID,
  }: {
    patientID?: string;
    patientResourceID?: string;
    systemURL?: string;
    patientUPID?: string;
  }): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'setPatient', {
        patientID,
        patientResourceID,
        systemURL,
        patientUPID,
      });
    }

    Telemetry.setGlobalContextProperty('patientUPID', patientUPID);
    Telemetry.setGlobalContextProperty('patientContext', {
      patientUPID,
      patientID,
      patientResourceID,
      systemURL,
    });
  }

  public static resetPatient(): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'resetPatient');
    }

    Telemetry.setGlobalContextProperty('patientContext', undefined);
    Telemetry.setGlobalContextProperty('patientUPID', undefined);
  }

  public static trackError({
    tag,
    message: maybeMessage,
    error,
    context = {},
  }: {
    tag: string;
    message?: string;
    error: Error | unknown;
    context?: TelemetryContext;
  }): void {
    if (!Telemetry.isInitialized) {
      Telemetry.deferredEvents.trackError.push({ tag, message: maybeMessage, error, context });
      return;
    }

    const serializedError = Telemetry.serializeError(error);
    const message = maybeMessage
      ? `${maybeMessage}: ${serializedError.message}`
      : serializedError.message;

    const errorContext: Record<string, unknown> = {
      serializedError,
      ...context,
    };

    if (error instanceof RequestError) {
      errorContext.statusCode = error.statusCode;
    }

    if (error instanceof ReactQueryError) {
      errorContext.query = {
        queryKeys: error.otherQueryKeys,
        identifier: error.identifier,
        requestErrors: error.requestErrors,
        responseErrors: error.responseErrors,
      };
    }

    Telemetry.logger.error(tag, message, {
      error: {
        ...errorContext,
      },
    });

    if (Telemetry.telemetryEnabled()) {
      datadogRum.addError(error, errorContext);
    }
  }

  public static trackInteraction(
    action: TelemetryUserInteraction,
    context: TelemetryContext = {},
  ): void {
    Telemetry.trackInteractionWithCondition(action, 'every_occurence', context);
  }

  public static trackInteractionWithCondition(
    action: TelemetryUserInteraction,
    condition: TelemetryInteractionCondition,
    context: TelemetryContext = {},
    deduplicationContext: TelemetryContext = {},
  ): void {
    if (!Telemetry.isInitialized) {
      Telemetry.deferredEvents.trackInteractionWithCondition.push([
        action,
        condition,
        context,
        deduplicationContext,
      ]);
      return;
    }

    Telemetry.reportActiveSession();

    const deduplicationContextKey = JSON.stringify(deduplicationContext);
    const interactionIdentifier = `${action}_${deduplicationContextKey}`;

    if (condition === 'once_per_session') {
      if (Telemetry.previouslyTrackedSessionInteractions.includes(interactionIdentifier)) {
        return;
      }

      Telemetry.previouslyTrackedSessionInteractions.push(interactionIdentifier);

      try {
        window.sessionStorage.setItem(
          'previouslyTrackedSessionInteractions',
          JSON.stringify(Telemetry.previouslyTrackedSessionInteractions),
        );
      } catch {
        /* pass */
      }
    }

    if (condition === 'once_per_page_view') {
      if (Telemetry.previouslyTrackedPageInteractions.includes(interactionIdentifier)) {
        return;
      }

      Telemetry.previouslyTrackedPageInteractions.push(interactionIdentifier);
    }

    if (Telemetry.telemetryEnabled()) {
      if (Telemetry.interactionLoggingEnabled) {
        Telemetry.logger.info(
          'Telemetry::trackInteractionWithCondition',
          `${action} [${condition}]`,
          context,
        );
      }

      datadogRum.addAction(action, context);
      try {
        amplitude
          .track(
            action,
            mapTelemetryContext('amplitude', {
              ...Telemetry.globalContextProperties,
              ...context,
            }),
          )
          .promise.then((result) => {
            if (Telemetry.interactionLoggingEnabled) {
              Telemetry.logger.debug('Telemetry', 'amplitude.track', { ...result });
            }
          })
          .catch((error) => {
            // Swallow amplitude failures so they don't leak as unhandled rejections;
            // DataDog already received the interaction above.
            Telemetry.logger.error('Telemetry', 'amplitude.track rejected', { error });
          });
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'amplitude.track threw synchronously', { error });
      }
    }
  }

  public static trackMetric(name: string, context: TelemetryContext = {}): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'trackMetric', {
        name,
        context,
      });
    }

    if (!Telemetry.isInitialized) {
      Telemetry.deferredEvents.trackMetric.push([name, context]);
      return;
    }

    if (Telemetry.telemetryEnabled()) {
      datadogRum.addAction(`ctw.metric.${name}`, context);
    }
  }

  public static withTimerMetric<TReturn, TContext extends TelemetryContext>(
    type: TelemetryMetricType,
    id: string,
    operationFn: (context: TContext & WithTimerMetricOperationFnContext) => Promise<TReturn>,
  ): (context: TContext) => Promise<TReturn> {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'withTimerMetric', {
        type,
        id,
        operationFn,
      });
    }

    return /* operationFnCurriedWithTimer */ async (context) => {
      const scopedContextProperties: TelemetryContext = {};

      const setScopedContextProperties = (properties: TelemetryContext) => {
        for (const [name, value] of Object.entries(properties)) {
          scopedContextProperties[name] = value;
        }
      };

      const setScopedContextProperty = (name: string, value: unknown) => {
        scopedContextProperties[name] = value;
      };

      const getScopedContextProperty = (name: string) => scopedContextProperties[name];

      const getScopedContextProperties = () => ({ ...scopedContextProperties });

      let returnValue: Awaited<TReturn> | undefined;
      try {
        Telemetry.startTimerMetric(type, id, scopedContextProperties);
        returnValue = await operationFn({
          ...context,
          setScopedContextProperties,
          setScopedContextProperty,
          getScopedContextProperty,
          getScopedContextProperties,
        });
      } catch (e) {
        returnValue = undefined;
        throw e;
      } finally {
        Telemetry.stopTimerMetric(type, id, scopedContextProperties);
      }

      return returnValue;
    };
  }

  public static startTimerMetric(
    type: TelemetryMetricType,
    id: string,
    context?: TelemetryContext,
  ): StartTimerMetricContext {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'startTimerMetric', {
        type,
        id,
        context,
      });
    }

    const vitalName = `ctw.${type}.${id}`;

    if (!Object.keys(Telemetry.timerMetrics).includes(vitalName)) {
      Telemetry.timerMetrics[vitalName] = {
        type,
        id,
        vitalName,
        context: context ?? {},
        startTime: Date.now(),
      };
    }

    const setScopedContextProperties = (properties: TelemetryContext) => {
      for (const [name, value] of Object.entries(properties)) {
        Telemetry.timerMetrics[vitalName].context[name] = value;
      }
    };

    const setScopedContextProperty = (name: string, value: unknown) => {
      Telemetry.timerMetrics[vitalName].context[name] = value;
    };

    const getScopedContextProperties = () => ({ ...Telemetry.timerMetrics[vitalName].context });

    const getScopedContextProperty = (name: string) =>
      Telemetry.timerMetrics[vitalName].context[name];

    return {
      setScopedContextProperties,
      getScopedContextProperty,
      getScopedContextProperties,
      setScopedContextProperty,
    };
  }

  public static stopTimerMetric(
    type: TelemetryMetricType,
    id: string,
    context?: TelemetryContext,
  ): number {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'stopTimerMetric', {
        type,
        id,
        context,
      });
    }

    const vitalName = `ctw.${type}.${id}`;

    if (Object.keys(Telemetry.timerMetrics).includes(vitalName)) {
      Telemetry.timerMetrics[vitalName].endTime = Date.now();
      Telemetry.timerMetrics[vitalName].context = {
        ...Telemetry.timerMetrics[vitalName].context,
        ...(context ?? {}),
      };

      const durationInSeconds =
        Math.abs(
          Telemetry.timerMetrics[vitalName].endTime - Telemetry.timerMetrics[vitalName].startTime,
        ) / 1000;

      if (Telemetry.telemetryEnabled() && Telemetry.isInitialized) {
        Telemetry.flushTimerMetric(vitalName);
      }

      return durationInSeconds;
    }

    return -1;
  }

  public static trackTimerMetric(
    type: TelemetryMetricType,
    id: TimerMetricIdentifier,
    startTime: number,
    endTime: number,
    context?: TelemetryContext,
  ): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'trackTimerMetric', {
        type,
        id,
        startTime,
        endTime,
        context,
      });
    }

    const vitalName = `ctw.${type}.${id}`;

    Telemetry.timerMetrics[vitalName] = {
      type,
      id,
      vitalName,
      context: context ?? {},
      startTime,
      endTime,
    };

    if (Telemetry.telemetryEnabled() && Telemetry.isInitialized) {
      Telemetry.flushTimerMetric(vitalName);
    }
  }

  public static redactSensitiveHttpHeaders(
    headers: HeadersInit | undefined,
  ): Record<string, unknown> {
    if (headers === undefined) {
      return {};
    }

    const headersRecord: Record<string, unknown> = Array.isArray(headers)
      ? Object.fromEntries(headers)
      : (headers as Record<string, unknown>);

    for (const [key, value] of Object.entries(headers)) {
      if (REDACTED_HEADERS.includes(key.toLowerCase() as never)) {
        headersRecord[key] = '[REDACTED]';
      } else {
        headersRecord[key] = value;
      }
    }

    return headersRecord;
  }

  private static flushTimerMetric(vitalName: string): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'flushTimerMetric', {
        vitalName,
      });
    }

    if (Object.keys(Telemetry.timerMetrics).includes(vitalName)) {
      const timerMetric = Telemetry.timerMetrics[vitalName];

      if (timerMetric.endTime !== undefined) {
        datadogRum.addDurationVital(vitalName, {
          startTime: timerMetric.startTime,
          duration: timerMetric.endTime - timerMetric.startTime,
          context: timerMetric.context,
        });
        delete Telemetry.timerMetrics[vitalName];
      }
    }
  }

  private static flushDeferredEvents(): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'flushDeferredEvents', {
        deferredEvents: { ...Telemetry.deferredEvents },
      });
    }

    const deferredEvents = { ...Telemetry.deferredEvents };
    Telemetry.deferredEvents = {
      setGlobalContextProperty: [],
      trackError: [],
      trackInteractionWithCondition: [],
      trackMetric: [],
      logger: [],
    };

    // Each replay is wrapped in try/catch so one SDK failing (typically
    // Amplitude) doesn't drop the remaining queued events destined for DataDog.
    for (const [key, value] of deferredEvents.setGlobalContextProperty) {
      try {
        Telemetry.setGlobalContextProperty(key, value);
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'flush setGlobalContextProperty failed', { error });
      }
    }

    for (const { tag, message, error, context } of deferredEvents.trackError) {
      try {
        Telemetry.trackError({
          tag,
          message,
          error,
          context: {
            ...context,
            deferred: true,
          },
        });
      } catch (replayError) {
        Telemetry.logger.error('Telemetry', 'flush trackError failed', { error: replayError });
      }
    }

    for (const [
      action,
      condition,
      context,
      deduplicationContext,
    ] of deferredEvents.trackInteractionWithCondition) {
      try {
        Telemetry.trackInteractionWithCondition(
          action,
          condition,
          { ...context, deferred: true },
          deduplicationContext,
        );
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'flush trackInteractionWithCondition failed', {
          error,
        });
      }
    }

    for (const [name, context] of deferredEvents.trackMetric) {
      try {
        Telemetry.trackMetric(name, { ...context, deferred: true });
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'flush trackMetric failed', { error });
      }
    }

    for (const metric of Object.values(Telemetry.timerMetrics)) {
      if (metric.endTime !== undefined) {
        try {
          Telemetry.flushTimerMetric(metric.vitalName);
        } catch (error) {
          Telemetry.logger.error('Telemetry', 'flush timerMetric failed', { error });
        }
      }
    }

    for (const [level, tag, message, context] of deferredEvents.logger) {
      try {
        switch (level) {
          case 'debug':
            Telemetry.logger.debug(tag, message, { ...context, deferred: true });
            break;
          case 'info':
            Telemetry.logger.info(tag, message, { ...context, deferred: true });
            break;
          case 'warn':
            Telemetry.logger.warn(tag, message, { ...context, deferred: true });
            break;
          case 'error':
            Telemetry.logger.error(tag, message, { ...context, deferred: true });
            break;
          default:
            Telemetry.logger.error('Telemetry', `Unknown log level: ${level}`, {
              level,
              tag,
              message,
              context,
              deferred: true,
            });
        }
      } catch (error) {
        Telemetry.logger.error('Telemetry', 'flush logger replay failed', { error });
      }
    }
  }

  public static determineTelemetryEnv(): TelemetryEnv {
    const { hostname } = window.location;

    for (const [env, patterns] of entries(telemetryEnvPatternMappings)) {
      for (const pattern of patterns) {
        if (pattern.test(hostname)) {
          return env as TelemetryEnv;
        }
      }
    }

    return 'dev';
  }

  private static serializeError(error: Error | unknown): {
    name: string;
    message: string;
    [key: string]: unknown;
  } {
    const serializedError: Record<string, unknown> = {};

    if (typeof error === 'object') {
      for (const propertyName of Object.getOwnPropertyNames(error)) {
        serializedError[propertyName] = (error as never)[propertyName];
      }
    }

    return {
      name: 'Unknown',
      message: 'Unknown',
      ...serializedError,
    };
  }

  private static initGlobalContextProperties(): void {
    const globalContextProperties = {
      ehr: Telemetry.requestContext.ehr,
      builderId: Telemetry.requestContext.builderId,
      service: Telemetry.requestContext.service,
      serviceEnv: Telemetry.requestContext.serviceEnv,
      serviceVersion: Telemetry.requestContext.serviceVersion,
      serviceVariant: Telemetry.requestContext.serviceVariant,
      telemetryEnv: Telemetry.telemetryEnv,
      vimEhrType: Telemetry.requestContext.ehrContext?.vimEhrType,
    };
    const userProperties = {
      id: Telemetry.requestContext.authTokenState.zusUserId,
      email: Telemetry.requestContext.authTokenState.email,
      type: Telemetry.requestContext.authTokenState.userType,
      practitionerId: Telemetry.requestContext.authTokenState.practitionerId,
    };

    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'initGlobalContextProperties', {
        globalContextProperties,
        userProperties,
      });
    }

    Telemetry.setGlobalContextProperties(globalContextProperties);

    Session.setSessionUserId(Telemetry.requestContext.authTokenState.zusUserId);

    // Isolate Amplitude identify so a failure there doesn't skip the DataDog
    // setUser calls below.
    try {
      const identifyEvent = new amplitude.Identify();
      for (const [rawProperty, value] of Object.entries(userProperties)) {
        const property = rawProperty === 'id' ? 'user_id' : rawProperty;
        identifyEvent.set(property, value);
      }
      amplitude.identify(identifyEvent);
    } catch (error) {
      Telemetry.logger.error('Telemetry', 'amplitude.identify failed', { error });
    }

    datadogLogs.setUser(userProperties);
    datadogRum.setUser(userProperties);
  }

  private static telemetryEnabled(): boolean {
    // See: apps/standalone/.env for the related ENV var (`NEXT_PUBLIC_DEV_TELEMETRY_ENABLED`)
    return (
      // Enable when service env is `prod`, `sandbox`, or `phitest` regardless of whether this is a local
      // dev environment or hosted environment
      ['prod', 'sandbox', 'phitest'].includes(Telemetry.requestContext.serviceEnv) ||
      // Enable when service env is `dev` and this is not a local dev environment
      (Telemetry.requestContext.serviceEnv === 'dev' && !currentHostnameIsLocalDevEnvironment()) ||
      // Enable when the window property `_zus_dev_telemetry_enabled` exists; this is set to `true`
      // when the standalone app env has `NEXT_PUBLIC_DEV_TELEMETRY_ENABLED=true`
      Object.hasOwn(window, '_zus_dev_telemetry_enabled')
    );
  }

  public static setGlobalContextProperty(key: string, value: unknown): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'setGlobalContextProperty', { key, value });
    }

    if (!Telemetry.isInitialized) {
      Telemetry.deferredEvents.setGlobalContextProperty.push([key, value]);
      return;
    }

    datadogLogs.setGlobalContextProperty(key, value);
    datadogRum.setGlobalContextProperty(key, value);

    if (value === undefined) {
      delete Telemetry.globalContextProperties[key];
    } else {
      Telemetry.globalContextProperties[key] = value;
    }
  }

  public static setGlobalContextProperties(properties: TelemetryContext): void {
    for (const [key, value] of Object.entries(properties)) {
      Telemetry.setGlobalContextProperty(key, value);
    }
  }

  public static getGlobalContextProperty(key: string): unknown {
    return Telemetry.globalContextProperties[key];
  }

  public static getGlobalContextProperties(): TelemetryContext {
    return { ...Telemetry.globalContextProperties };
  }

  // Override the name DataDog RUM gives the current view. Apps drive this on route
  // change to report canonical, groupable view names (see computeCanonicalRumViewName).
  // `null` means the current route has no canonical name: any pending override is
  // discarded so a later init doesn't apply a stale name to the wrong view, and
  // DataDog's automatic naming is left in place.
  public static setViewName(name: string | null): void {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'setViewName', { name });
    }

    if (name === null) {
      Telemetry.pendingViewName = undefined;
      return;
    }

    // Retain the name even before init: the route effect requesting it usually
    // fires before the async init resolves, and performInit() applies the pending
    // name once RUM is up. The isInitialized check short-circuits telemetryEnabled(),
    // which reads requestContext and would throw before init.
    Telemetry.pendingViewName = name;

    if (Telemetry.isInitialized && Telemetry.telemetryEnabled()) {
      datadogRum.setViewName(name);
    }
  }

  public static beforeNavigate() {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'beforeNavigate');
    }

    Telemetry.previouslyTrackedPageInteractions = [];
    amplitude.flush();
  }

  public static afterNavigate() {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'afterNavigate');
    }

    amplitude.flush();
  }

  public static beforeLogOut() {
    if (Telemetry.interactionLoggingEnabled) {
      Telemetry.logger.debug('Telemetry', 'beforeLogOut');
    }

    amplitude.reset();
  }

  private static reportActiveSession() {
    try {
      if (Session.isActive()) {
        return;
      }

      Session.setSessionUserId(Telemetry.requestContext.authTokenState.zusUserId);
      Session.setSessionLastActiveTimestamp();
    } catch (error) {
      Telemetry.trackError({
        tag: 'Telemetry',
        message: 'Telemetry error reporting active session',
        error,
      });
    }
  }

  public static debugState(): Record<string, unknown> {
    return {
      telemetryEnabled: Telemetry.telemetryEnabled(),
      devTelemetryEnabled: window._zus_dev_telemetry_enabled,
      reinitializationCount: Telemetry.reinitializationCount,
      requestContext: Telemetry.requestContext,
      isInitialized: Telemetry.isInitialized,
      isAmplitudeInitialized: Telemetry.isAmplitudeInitialized,
      isDatadogRumInitialized: Telemetry.isDatadogRumInitialized,
      isDatadogLoggingInitialized: Telemetry.isDatadogLoggingInitialized,
      interactionLoggingEnabled: Telemetry.interactionLoggingEnabled,
      configHash: Telemetry.configHash,
      telemetryEnv: Telemetry.telemetryEnv,
      globalContextProperties: Telemetry.globalContextProperties,
      previouslyTrackedSessionInteractions: Telemetry.previouslyTrackedSessionInteractions,
      previouslyTrackedPageInteractions: Telemetry.previouslyTrackedPageInteractions,
      deferredEvents: Telemetry.deferredEvents,
      timerMetrics: Telemetry.timerMetrics,
    };
  }

  private static getConfigHash(config: object) {
    return JSON.stringify(config)
      .split('')
      .reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);
  }
}
